/**
 * Department code → the name the structure gave that department.
 *
 * Shifts, SOPs, tasks and memberships all store the department **code** (the
 * doc id under `hotels/{hotelId}/departments/{code}`), never its name: the name
 * can be renamed, the code cannot. Every surface that shows a department to a
 * person must therefore resolve the code against the hotel's department docs,
 * and must do it the same way — this is that one way.
 *
 * The name lives in `displayName`: that is the field the organization API
 * (`POST/PUT /hotels/:id/departments`), the signup seed and the onboarding
 * wizard write. `name` is what the shared `Department` type declares and what
 * the older demo seeds wrote; it is read as a second choice so those tenants
 * keep their labels. Reading only `name` is the defect this helper replaces:
 * departments created from the app have no `name`, so every screen fell back
 * to the raw code («housekeeping», «HK») or to an empty option.
 *
 * A code that matches no department is never shown as-is: the person reading
 * the screen did not choose it and cannot act on it.
 */
export interface DepartmentNameSource {
  readonly code: string;
  readonly displayName?: string | undefined;
  readonly name?: string | undefined;
}

/** Shown when the code matches no department of the loaded hotel(s). */
export const UNKNOWN_DEPARTMENT_LABEL = "Reparto non trovato";

/**
 * Shown while there is nothing to resolve against: departments not loaded yet,
 * failed to load, or none configured. A list that is still arriving must not
 * claim the department is missing.
 */
export const PENDING_DEPARTMENT_LABEL = "Reparto";

/** The configured name of one department doc, or the unknown label. */
export function departmentName(dept: DepartmentNameSource | undefined): string {
  const label = dept?.displayName?.trim() || dept?.name?.trim();
  return label || UNKNOWN_DEPARTMENT_LABEL;
}

/**
 * Build a `code → name` lookup over a hotel's (or several hotels') department
 * docs. With an empty list every code reads as `PENDING_DEPARTMENT_LABEL`.
 */
export function departmentNameResolver(
  departments: ReadonlyArray<DepartmentNameSource>,
): (code: string) => string {
  const byCode = new Map(departments.map((d) => [d.code, d]));
  return (code) =>
    departments.length === 0 ? PENDING_DEPARTMENT_LABEL : departmentName(byCode.get(code));
}
